import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiActivity, FiArrowDownRight, FiArrowUpRight, FiBookOpen, FiClock } from 'react-icons/fi';
import api from '../../services/api';
import PageLoading from '../PageLoading';
import SimpleAreaChart from '../common/SimpleAreaChart';
import { formatScore } from '../../utils/scoreFormat';
import {
  buildAnalyticsCatalog,
  getLastActivityText,
  getProgressSummary,
} from '../../utils/testAnalytics';
import './TestAnalytics.css';

function resultDetailHref({ resultId, analyticsContext, clientUserId }) {
  if (!resultId) {
    return null;
  }
  if (analyticsContext === 'client' && clientUserId) {
    return `/psychologist/users/${clientUserId}/results/${resultId}`;
  }
  if (analyticsContext === 'self') {
    return `/results/${resultId}`;
  }
  return null;
}

function ChartTooltipContent({ point, analyticsContext, clientUserId }) {
  if (!point) {
    return null;
  }
  const href = resultDetailHref({ resultId: point.resultId, analyticsContext, clientUserId });
  return (
    <div className="analytics-chart-tooltip pointer-events-auto">
      <div className="analytics-chart-tooltip-row">
        <strong>Прохождение:</strong> {point.passingLabel || '—'}
      </div>
      <div className="analytics-chart-tooltip-row">
        <strong>Балл:</strong> {formatScore(point.score)}
      </div>
      {point.level ? (
        <div className="analytics-chart-tooltip-row">
          <strong>Уровень:</strong> {point.level}
        </div>
      ) : null}
      {href ? (
        <div className="analytics-chart-tooltip-footer">
          <Link
            to={href}
            className="analytics-chart-tooltip-link"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            Страница результата
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.embedded]
 * @param {object[]|undefined} [props.resultsOverride] — если задан (включая []), данные не запрашиваются с API
 * @param {boolean} [props.overrideLoading] — при resultsOverride: показать загрузку (например, пока грузятся результаты клиента)
 * @param {string} [props.overrideError] — при resultsOverride: текст ошибки с родителя
 * @param {'self'|'client'} [props.analyticsContext] — копирайт пустого состояния: свой кабинет или просмотр клиента
 * @param {string} [props.clientUserId] — при analyticsContext client: id пользователя для ссылок /psychologist/users/…/results/…
 */
function TestAnalytics({
  embedded = false,
  resultsOverride,
  overrideLoading = false,
  overrideError = '',
  analyticsContext = 'self',
  clientUserId,
}) {
  const [results, setResults] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [selectedTestIdentity, setSelectedTestIdentity] = useState('');
  const [loading, setLoading] = useState(resultsOverride === undefined);
  const [error, setError] = useState('');

  const usesOverride = resultsOverride !== undefined;

  useEffect(() => {
    if (usesOverride) {
      return;
    }
    const loadResults = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await api.getResults();
        setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || 'Ошибка загрузки аналитики');
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [usesOverride]);

  useEffect(() => {
    if (!usesOverride) {
      return;
    }
    if (overrideLoading) {
      setLoading(true);
      return;
    }
    setLoading(false);
    setError(overrideError || '');
    setResults(Array.isArray(resultsOverride) ? resultsOverride : []);
  }, [usesOverride, resultsOverride, overrideLoading, overrideError]);

  const categories = useMemo(() => buildAnalyticsCatalog(results), [results]);

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategoryName('');
      setSelectedTestIdentity('');
      return;
    }

    const hasSelectedCategory = categories.some((category) => category.name === selectedCategoryName);
    const category = hasSelectedCategory ? categories.find((item) => item.name === selectedCategoryName) : categories[0];

    if (!hasSelectedCategory) {
      setSelectedCategoryName(category.name);
    }

    const hasSelectedTest = category.tests.some((test) => test.identity === selectedTestIdentity);
    if (!hasSelectedTest) {
      setSelectedTestIdentity(category.tests[0]?.identity || '');
    }
  }, [categories, selectedCategoryName, selectedTestIdentity]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.name === selectedCategoryName) || null,
    [categories, selectedCategoryName]
  );
  const selectedTest = useMemo(
    () => selectedCategory?.tests.find((test) => test.identity === selectedTestIdentity) || null,
    [selectedCategory, selectedTestIdentity]
  );
  const progressSummary = useMemo(() => getProgressSummary(selectedTest?.points || []), [selectedTest]);
  const lastActivity = useMemo(() => getLastActivityText(selectedTest?.points || []), [selectedTest]);

  if (loading) {
    return (
      <div className={`${embedded ? '' : 'container mt-4'} text-center`}>
        <PageLoading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? '' : 'container mt-4'}>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!categories.length) {
    return (
      <div className={embedded ? '' : 'container mt-4'}>
        <div className="alert alert-info">
          {analyticsContext === 'client' ? (
            <>У пользователя пока нет данных для аналитики. После прохождения тестов здесь появится динамика результатов.</>
          ) : (
            <>
              Пока нет данных для аналитики. <Link to="/tests">Пройдите первый тест</Link>, и мы построим динамику.
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'container mt-4'} analytics-page`}>
      {!embedded && (
        <div className="analytics-page-header ui-card">
          <h2>Аналитика состояния</h2>
          <p>Выберите категорию и методику, чтобы увидеть динамику результатов.</p>
        </div>
      )}

      <div className="analytics-layout">
        <aside className="analytics-sidebar">
          <div className="analytics-card ui-card">
            <h3>1. Категории</h3>
            <div className="analytics-button-list">
              {categories.map((category) => (
                <button
                  key={category.name}
                  type="button"
                  onClick={() => {
                    setSelectedCategoryName(category.name);
                    setSelectedTestIdentity(category.tests[0]?.identity || '');
                  }}
                  className={`analytics-choice-btn ${selectedCategoryName === category.name ? 'is-active' : ''}`}
                >
                  <span>{category.name}</span>
                  <span>{category.tests.length}</span>
                </button>
              ))}
            </div>

            <h3 className="mt-4">2. Методики</h3>
            <div className="analytics-button-list">
              {selectedCategory?.tests.map((test) => (
                <button
                  key={test.identity}
                  type="button"
                  onClick={() => setSelectedTestIdentity(test.identity)}
                  className={`analytics-choice-btn is-secondary ${selectedTestIdentity === test.identity ? 'is-active' : ''}`}
                >
                  {test.title}
                </button>
              ))}
            </div>
          </div>

          <div className="analytics-info-card ui-card">
            <div className="analytics-info-head">
              <FiBookOpen />
              <h4>О методике</h4>
            </div>
            <p>{selectedTest?.description || 'Описание методики недоступно.'}</p>
          </div>
        </aside>

        <section className="analytics-main">
          <div className="analytics-card analytics-chart-card ui-card">
            <div className="analytics-chart-head">
              <div>
                <div className="analytics-chart-tag">
                  <FiActivity />
                  Динамика
                </div>
                <h3>{selectedTest?.title || 'Методика'}</h3>
              </div>
              <div className="analytics-score">
                <span>Результат</span>
                <strong>{selectedTest?.points.at(-1)?.score ?? '--'}</strong>
              </div>
            </div>

            <div className="analytics-chart-wrap">
              <SimpleAreaChart
                data={selectedTest?.points || []}
                xKey="xAxisLabel"
                yKey="score"
                stroke="#6e9abe"
                height={280}
                renderTooltip={(point) => (
                  <ChartTooltipContent
                    point={point}
                    analyticsContext={analyticsContext}
                    clientUserId={clientUserId}
                  />
                )}
              />
            </div>

            {selectedTest?.points?.length > 0 &&
              selectedTest.points.some((pt) =>
                resultDetailHref({ resultId: pt.resultId, analyticsContext, clientUserId }),
              ) && (
                <div className="analytics-chart-passings-links">
                  <span className="analytics-chart-passings-label">К результатам:</span>
                  <div className="analytics-chart-passings-pills">
                    {selectedTest.points.map((p, idx) => {
                      const href = resultDetailHref({
                        resultId: p.resultId,
                        analyticsContext,
                        clientUserId,
                      });
                      if (!href) {
                        return null;
                      }
                      return (
                        <Link
                          key={p.resultId || p.id || `${idx}-${p.rawDate}`}
                          to={href}
                          className="analytics-chart-passings-pill"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {p.passingLabel || p.xAxisLabel}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>

          {selectedTest?.points?.length > 0 && (
            <div className="analytics-passings-card ui-card">
              <h4 className="analytics-passings-title">Прохождения на графике</h4>
              <p className="analytics-passings-hint text-muted small">
                Каждая точка соответствует одному сохранённому результату теста (дата и время на сервере).
              </p>
              <ol className="analytics-passings-list">
                {selectedTest.points.map((p, idx) => {
                  const rowHref = resultDetailHref({ resultId: p.resultId, analyticsContext, clientUserId });
                  return (
                    <li key={p.resultId || p.id || `${idx}-${p.rawDate}`}>
                      <span className="analytics-passings-when">{p.passingLabel}</span>
                      <span className="analytics-passings-score"> — {formatScore(p.score)}</span>
                      {p.level ? <span className="text-muted"> ({p.level})</span> : null}
                      {rowHref ? (
                        <>
                          {' '}
                          <Link
                            to={rowHref}
                            className="analytics-passings-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Страница результата
                          </Link>
                        </>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          <div className="analytics-bottom-cards">
            <div className={`analytics-status-card status-${progressSummary.tone}`}>
              <div className="analytics-status-head">
                {progressSummary.tone === 'success' ? <FiArrowDownRight /> : <FiArrowUpRight />}
                <span>{progressSummary.label}</span>
              </div>
              <p>{progressSummary.description}</p>
            </div>

            <div className="analytics-status-card status-neutral">
              <div className="analytics-status-head">
                <FiClock />
                <span>Активность</span>
              </div>
              <p>{lastActivity}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default TestAnalytics;
